import React, { useEffect, useState } from "react";
import { authApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { X, UserPlus, Trash2, Shield, Wand2, Copy, KeyRound } from "lucide-react";

const genPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%";
  const arr = new Uint32Array(14);
  (window.crypto || window.msCrypto).getRandomValues(arr);
  return Array.from(arr, (n) => chars[n % chars.length]).join("");
};

export const TeamModal = ({ open, onClose }) => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = () => authApi.users().then(setUsers).catch(() => {});
  useEffect(() => { if (open) { load(); setEmail(""); setName(""); setPassword(""); setResetFor(null); setNewPw(""); } }, [open]);
  if (!open) return null;

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await authApi.addUser({ email: email.trim(), name: name.trim(), password });
      toast.success(`Added ${email.trim()}`);
      setEmail(""); setName(""); setPassword("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not add user"); }
    finally { setBusy(false); }
  };

  const remove = async (u) => {
    try { await authApi.removeUser(u.id); toast.success(`Removed ${u.email}`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not remove user"); }
  };

  const saveReset = async (u) => {
    if (newPw.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    try {
      await authApi.setPassword(u.id, newPw);
      toast.success(`Password updated for ${u.email}${u.id === user?.id ? " — you'll stay signed in on this device" : ""}`);
      setResetFor(null); setNewPw("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not update password"); }
    finally { setBusy(false); }
  };
  const openReset = (u) => { setResetFor(resetFor === u.id ? null : u.id); setNewPw(""); };

  return (
    <div className="modal-overlay" data-testid="team-modal" onMouseDown={onClose}>
      <div className="modal-card" style={{ maxWidth: 560, width: "94%" }} onMouseDown={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <div className="modal-title" style={{ marginBottom: 0 }}>Team access</div>
          <button className="btn btn-sm" onClick={onClose} data-testid="team-close"><X size={14} /></button>
        </div>
        <div className="modal-sub">Add or remove teammates who can sign in to this workspace.</div>

        <form onSubmit={add} style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 14 }}>
          <div style={{ flex: "1 1 160px" }}>
            <label className="login-label">Email</label>
            <input className="inp" type="email" placeholder="teammate@xyzvtr.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="team-email" />
          </div>
          <div style={{ flex: "1 1 110px" }}>
            <label className="login-label">Name</label>
            <input className="inp" placeholder="Optional" value={name} onChange={(e) => setName(e.target.value)} data-testid="team-name" />
          </div>
          <div style={{ flex: "1 1 150px" }}>
            <label className="login-label">Password</label>
            <div style={{ display: "flex", gap: 4 }}>
              <input className="inp" type="text" placeholder="min 6 chars" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="team-password" />
              <button type="button" className="btn" style={{ padding: "0 8px", flex: "0 0 auto" }} title="Generate a strong password" onClick={() => { const p = genPassword(); setPassword(p); navigator.clipboard?.writeText(p)?.catch(() => {}); toast.success("Strong password generated & copied"); }} data-testid="team-gen-password"><Wand2 size={13} /></button>
            </div>
          </div>
          <button className="btn btn-primary" type="submit" disabled={busy} data-testid="team-add"><UserPlus size={13} style={{ marginRight: 4 }} />{busy ? "Adding…" : "Add"}</button>
        </form>
        {password && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, margin: "-6px 0 14px", fontSize: 11, color: "var(--text2)" }} data-testid="team-password-hint">
            <Copy size={11} /> Share this password with the teammate — they can change it later. It won't be shown again after you add them.
          </div>
        )}

        <div className="section-label">Members ({users.length})</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6, maxHeight: 260, overflowY: "auto" }}>
          {users.map((u) => (
            <div key={u.id} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div className="attach-chip" style={{ justifyContent: "space-between" }} data-testid={`team-row-${u.id}`}>
                <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                  {u.role === "admin" && <Shield size={12} style={{ color: "var(--lime)" }} />}
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.name || u.email} <span style={{ color: "var(--text3)" }}>· {u.email}</span></span>
                  {u.id === user?.id && <span className="tag" style={{ fontSize: 9 }}>you</span>}
                </span>
                <span style={{ display: "flex", gap: 4, flex: "0 0 auto" }}>
                  <button className="btn" style={{ padding: "2px 6px" }} title={u.id === user?.id ? "Change your password" : `Reset password for ${u.email}`} onClick={() => openReset(u)} data-testid={`team-reset-${u.id}`}><KeyRound size={13} /></button>
                  {u.id !== user?.id && (
                    <button className="btn" style={{ padding: "2px 6px" }} title={`Remove ${u.email}`} onClick={() => remove(u)} data-testid={`team-remove-${u.id}`}><Trash2 size={13} /></button>
                  )}
                </span>
              </div>
              {resetFor === u.id && (
                <div style={{ display: "flex", gap: 4, alignItems: "center", padding: "0 4px 2px" }} data-testid={`team-reset-panel-${u.id}`}>
                  <input className="inp" type="text" placeholder="New password (min 6 chars)" value={newPw} onChange={(e) => setNewPw(e.target.value)} style={{ flex: 1 }} data-testid={`team-reset-input-${u.id}`} />
                  <button type="button" className="btn" style={{ padding: "0 8px", flex: "0 0 auto" }} title="Generate a strong password" onClick={() => { const p = genPassword(); setNewPw(p); navigator.clipboard?.writeText(p)?.catch(() => {}); toast.success("Strong password generated & copied"); }} data-testid={`team-reset-gen-${u.id}`}><Wand2 size={13} /></button>
                  <button className="btn btn-primary" disabled={busy} onClick={() => saveReset(u)} data-testid={`team-reset-save-${u.id}`}>{busy ? "Saving…" : "Save"}</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
